/**
 * XTREME CANOPIES - MOBILE TOUCH INTERACTIONS 2025
 * Swipe gestures, pull-to-close, and smooth mobile interactions
 * Performance optimized for all mobile devices
 */

(function() {
  'use strict';

  // Only run on touch devices
  if (!('ontouchstart' in window)) return;

  // Configuration
  const SWIPE_THRESHOLD = 100; // pixels
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // pixels per millisecond
  const PULL_TO_CLOSE_THRESHOLD = 150; // pixels

  /**
   * Swipe Detection Class
   * Handles swipe gestures with velocity calculation
   */
  class SwipeDetector {
    constructor(element, options = {}) {
      this.element = element;
      this.onSwipeUp = options.onSwipeUp || (() => {});
      this.onSwipeDown = options.onSwipeDown || (() => {});
      this.onSwipeLeft = options.onSwipeLeft || (() => {});
      this.onSwipeRight = options.onSwipeRight || (() => {});

      this.startX = 0;
      this.startY = 0;
      this.startTime = 0;
      this.distX = 0;
      this.distY = 0;
      this.elapsedTime = 0;

      this.init();
    }

    init() {
      this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.element.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    }

    handleTouchStart(e) {
      const touch = e.touches[0];
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.startTime = Date.now();
    }

    handleTouchMove(e) {
      if (!this.startX || !this.startY) return;

      const touch = e.touches[0];
      this.distX = touch.clientX - this.startX;
      this.distY = touch.clientY - this.startY;
    }

    handleTouchEnd(e) {
      if (!this.startX || !this.startY) return;

      this.elapsedTime = Date.now() - this.startTime;

      const absDistX = Math.abs(this.distX);
      const absDistY = Math.abs(this.distY);
      const velocity = Math.max(absDistX, absDistY) / this.elapsedTime;

      // Determine if it's a valid swipe
      const isSwipe = (absDistX > SWIPE_THRESHOLD || absDistY > SWIPE_THRESHOLD) &&
                      velocity > SWIPE_VELOCITY_THRESHOLD;

      if (isSwipe) {
        // Determine direction (prioritize horizontal or vertical based on distance)
        if (absDistX > absDistY) {
          // Horizontal swipe
          if (this.distX > 0) {
            this.onSwipeRight(this.distX, velocity);
          } else {
            this.onSwipeLeft(Math.abs(this.distX), velocity);
          }
        } else {
          // Vertical swipe
          if (this.distY > 0) {
            this.onSwipeDown(this.distY, velocity);
          } else {
            this.onSwipeUp(Math.abs(this.distY), velocity);
          }
        }
      }

      // Reset
      this.startX = 0;
      this.startY = 0;
      this.distX = 0;
      this.distY = 0;
      this.startTime = 0;
    }

    destroy() {
      this.element.removeEventListener('touchstart', this.handleTouchStart);
      this.element.removeEventListener('touchmove', this.handleTouchMove);
      this.element.removeEventListener('touchend', this.handleTouchEnd);
    }
  }

  /**
   * Initialize swipe-to-close for search overlay
   */
  function initializeSearchOverlaySwipe() {
    const overlay = document.querySelector('.mobile-search-overlay');
    if (!overlay) return;

    // Swipe down to close
    const swipeDetector = new SwipeDetector(overlay, {
      onSwipeDown: (distance, velocity) => {
        // Close overlay on swipe down
        if (distance > PULL_TO_CLOSE_THRESHOLD || velocity > 0.5) {
          // Trigger Alpine.js to close the overlay
          const event = new CustomEvent('close-search-overlay');
          document.dispatchEvent(event);

          // Also close via Alpine.js data
          const alpineElement = document.querySelector('[x-data]');
          if (alpineElement && alpineElement.__x) {
            alpineElement.__x.$data.searchOpen = false;
          }
        }
      }
    });

    // Visual feedback during swipe
    let startY = 0;
    let currentY = 0;

    overlay.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
    }, { passive: true });

    overlay.addEventListener('touchmove', (e) => {
      if (!overlay.classList.contains('active')) return;

      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // Only allow downward swipes
      if (deltaY > 0) {
        const container = overlay.querySelector('.mobile-search-container');
        if (container) {
          // Apply transform for visual feedback
          const translateY = Math.min(deltaY * 0.5, 200); // Max 200px
          const opacity = Math.max(1 - (deltaY / 400), 0.3); // Min 0.3 opacity

          container.style.transform = `translateY(${translateY}px)`;
          overlay.style.opacity = opacity;
        }
      }
    }, { passive: true });

    overlay.addEventListener('touchend', () => {
      // Reset visual feedback
      const container = overlay.querySelector('.mobile-search-container');
      if (container) {
        container.style.transform = '';
        overlay.style.opacity = '';
      }
    }, { passive: true });
  }

  /**
   * Initialize swipe navigation for mobile drawer
   */
  function initializeDrawerSwipe() {
    const drawer = document.querySelector('.xc-drawer');
    if (!drawer) return;

    // Swipe left to close drawer
    const swipeDetector = new SwipeDetector(drawer, {
      onSwipeLeft: (distance, velocity) => {
        if (distance > SWIPE_THRESHOLD || velocity > 0.5) {
          // Trigger Alpine.js to close the drawer
          const alpineElement = document.querySelector('[x-data]');
          if (alpineElement && alpineElement.__x) {
            alpineElement.__x.$data.drawerOpen = false;
          }
        }
      }
    });
  }

  /**
   * Add haptic feedback for touch interactions (if supported)
   */
  function addHapticFeedback(element) {
    if (!element || !navigator.vibrate) return;

    element.addEventListener('touchstart', () => {
      // Light haptic feedback (10ms)
      navigator.vibrate(10);
    }, { passive: true });
  }

  /**
   * Initialize haptic feedback for interactive elements
   */
  function initializeHapticFeedback() {
    // Add haptic feedback to bottom nav items
    const navLinks = document.querySelectorAll('.mobile-bottom-nav__link');
    navLinks.forEach(link => addHapticFeedback(link));

    // Add haptic feedback to search button
    const searchBtn = document.querySelector('.xc-mobile-search-btn');
    if (searchBtn) addHapticFeedback(searchBtn);

    // Add haptic feedback to hamburger menu
    const hamburger = document.querySelector('.xc-hamburger');
    if (hamburger) addHapticFeedback(hamburger);
  }

  /**
   * Prevent body scroll when overlay is open
   */
  function initializeScrollLock() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('overflow-hidden')) {
            // Prevent background scroll
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
          } else {
            // Restore scroll
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
          }
        }
      });
    });

    const bodyElement = document.body;
    if (bodyElement) {
      observer.observe(bodyElement, { attributes: true });
    }
  }

  /**
   * Auto-focus search input when overlay opens
   */
  function initializeSearchAutoFocus() {
    const overlay = document.querySelector('.mobile-search-overlay');
    if (!overlay) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('active')) {
            // Delay focus to allow animation to complete
            setTimeout(() => {
              const searchInput = target.querySelector('.mobile-search-input');
              if (searchInput && window.innerWidth <= 768) {
                searchInput.focus();
              }
            }, 300);
          }
        }
      });
    });

    observer.observe(overlay, { attributes: true });
  }

  /**
   * Initialize all touch interactions
   */
  function initialize() {
    // Swipe gestures
    initializeSearchOverlaySwipe();
    initializeDrawerSwipe();

    // Haptic feedback
    initializeHapticFeedback();

    // Scroll lock
    initializeScrollLock();

    // Auto-focus
    initializeSearchAutoFocus();

    console.log('Mobile touch interactions initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Re-initialize on navigation (for SPA-like behavior)
  document.addEventListener('shopify:section:load', initialize);

})();
